import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { BarElement, CategoryScale, Chart as ChartJS, LinearScale, Tooltip, type ChartOptions } from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { AlertCircle, Gauge, Loader2, Lock, RefreshCw } from 'lucide-react'

import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import { Button } from 'components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card'
import { Select } from 'components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table'
import { cn } from 'lib/utils'
import { getAiUsage, type AiUsage } from 'services/adminAiUsageService'

import {
	OUTCOME_LABELS,
	RANGE_OPTIONS,
	capTone,
	formatCount,
	formatMs,
	formatPercent,
	formatTokens,
	formatUsd,
	rangeFor,
	type AiUsageRangeKey,
} from './aiUsageFormat'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip)

/**
 * AI usage (MYD-630): what Ask MDT is costing, and how it is behaving. Admins
 * only. The server refuses anyone else (isAdmin); this page also says so
 * rather than show an empty dashboard.
 *
 * Read from the usage ledger (one row per call to Claude). Costs are the
 * ledger's estimates from the price table; a call to a model the table does
 * not price is counted, not costed, and shown as such. The monthly bar is
 * counted the way AI_MONTHLY_USD_CAP counts, so it matches what stops
 * requests.
 */

const calls = (n: number) => `${formatCount(n)} ${n === 1 ? 'call' : 'calls'}`

/** The primary token, for the chart: canvas cannot read Tailwind classes. */
const primaryColor = () => {
	const hsl = typeof window !== 'undefined' ? getComputedStyle(document.documentElement).getPropertyValue('--primary').trim() : ''
	return hsl ? `hsl(${hsl})` : 'hsl(210 100% 50%)'
}

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
	return (
		<Card>
			<CardContent className="p-4">
				<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
				<p className="mt-1 text-2xl font-semibold text-gray-900">{value}</p>
				{hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
			</CardContent>
		</Card>
	)
}

function CapBar({ month }: { month: AiUsage['month'] }) {
	const tone = capTone(month.share)
	const width = Math.min(100, Math.round((month.share ?? 0) * 100))
	return (
		<Card>
			<CardHeader className="pb-2">
				<CardTitle className="flex items-center gap-2 text-base">
					<Gauge className="h-4 w-4 text-primary" aria-hidden="true" />
					This month against the cap
				</CardTitle>
			</CardHeader>
			<CardContent className="space-y-2">
				<div
					role="progressbar"
					aria-label="Spend this month against the monthly cap"
					aria-valuemin={0}
					aria-valuemax={month.capUsd}
					aria-valuenow={Number(month.spentUsd.toFixed(2))}
					aria-valuetext={`${formatUsd(month.spentUsd)} of ${formatUsd(month.capUsd)}`}
					className="h-2.5 w-full overflow-hidden rounded-full bg-muted"
				>
					<div className={cn('h-full rounded-full', tone === 'over' ? 'bg-destructive' : tone === 'warn' ? 'bg-warning' : 'bg-primary')} style={{ width: `${width}%` }} />
				</div>
				<p className="text-sm text-gray-700">
					<span className="font-semibold">{formatUsd(month.spentUsd)}</span> of {formatUsd(month.capUsd)} since {month.start} (UTC)
					{month.share != null && <span className="text-muted-foreground"> · {formatPercent(month.share)}</span>}
				</p>
				{month.unpricedCeilingUsd > 0 && (
					<p className="text-xs text-muted-foreground">Includes {formatUsd(month.unpricedCeilingUsd)} for calls to unpriced models, counted at the dearest price, as the cap counts them.</p>
				)}
				{tone === 'over' && <p className="text-xs font-medium text-destructive">The cap is reached: Ask MDT refuses new requests until next month.</p>}
			</CardContent>
		</Card>
	)
}

function DailyCost({ days }: { days: AiUsage['byDay'] }) {
	const data = useMemo(
		() => ({
			labels: days.map(day => day.day.slice(5)),
			datasets: [{ label: 'Cost', data: days.map(day => day.costUsd), backgroundColor: primaryColor(), borderRadius: 3, maxBarThickness: 28 }],
		}),
		[days],
	)
	const options: ChartOptions<'bar'> = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { display: false },
			tooltip: {
				callbacks: {
					title: items => days[items[0].dataIndex]?.day ?? '',
					label: item => `${formatUsd(Number(item.raw))} · ${calls(days[item.dataIndex]?.calls ?? 0)}`,
				},
			},
		},
		scales: {
			x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkipPadding: 12 } },
			y: { beginAtZero: true, ticks: { callback: value => formatUsd(Number(value)) } },
		},
	}
	const total = days.reduce((sum, day) => sum + day.costUsd, 0)
	return (
		<Card>
			<CardHeader className="pb-2">
				<CardTitle className="text-base">Daily cost</CardTitle>
			</CardHeader>
			<CardContent>
				{/* A canvas says nothing to a screen reader: the range and total are given as a sentence. */}
				<div className="h-56" role="img" aria-label={`Daily AI cost over ${days.length} days, ${formatUsd(total)} in all`}>
					<Bar data={data} options={options} />
				</div>
			</CardContent>
		</Card>
	)
}

export default function AiUsageCN() {
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const user = useSelector((state: any) => state.userLogin?.userInfo)
	const [rangeKey, setRangeKey] = useState<AiUsageRangeKey>('30d')
	const [usage, setUsage] = useState<AiUsage | null>(null)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [reloads, setReloads] = useState(0)
	const isAdmin = Boolean(user?.isAdmin)

	useEffect(() => {
		if (!isAdmin) return
		let live = true
		setLoading(true)
		setError(null)
		getAiUsage(rangeFor(rangeKey))
			.then(result => live && setUsage(result))
			.catch(() => live && setError('AI usage could not be loaded. Try again in a moment.'))
			.finally(() => live && setLoading(false))
		return () => {
			live = false
		}
	}, [isAdmin, rangeKey, reloads])

	const totals = usage?.totals
	const tokens = totals ? totals.inputTokens + totals.outputTokens + totals.cacheReadTokens + totals.cacheWriteTokens : null

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />
			<div className="flex flex-1 flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />
				<main className="flex-1 space-y-4 overflow-y-auto p-6">
					<div className="flex flex-wrap items-center justify-between gap-3">
						<div>
							<h2 className="text-2xl font-semibold text-gray-800">AI usage</h2>
							<p className="text-sm text-muted-foreground">What Ask MDT is costing, and how it is behaving. Days are UTC.</p>
						</div>
						{isAdmin && (
							<div className="flex items-center gap-2">
								<label htmlFor="ai-usage-range" className="sr-only">
									Range
								</label>
								<Select id="ai-usage-range" value={rangeKey} onChange={event => setRangeKey(event.target.value as AiUsageRangeKey)} className="w-40">
									{RANGE_OPTIONS.map(option => (
										<option key={option.key} value={option.key}>
											{option.label}
										</option>
									))}
								</Select>
								<Button variant="ghost" isOutline onClick={() => setReloads(n => n + 1)} disabled={loading} aria-label="Refresh">
									{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
								</Button>
							</div>
						)}
					</div>

					{!isAdmin ? (
						<div className="flex flex-col items-center justify-center py-16 text-center">
							<div className="mb-4 rounded-full bg-gray-100 p-3">
								<Lock className="h-6 w-6 text-gray-400" aria-hidden="true" />
							</div>
							<h3 className="mb-1 text-lg font-medium text-gray-900">Admins only</h3>
							<p className="max-w-sm text-sm text-gray-500">AI usage covers every account's requests and costs, so it is shown to administrators only.</p>
						</div>
					) : error ? (
						<div role="alert" className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
							<AlertCircle className="h-4 w-4" aria-hidden="true" />
							{error}
						</div>
					) : !usage ? (
						<p className="flex items-center gap-2 text-sm text-muted-foreground">
							<Loader2 className="h-4 w-4 animate-spin" /> Loading…
						</p>
					) : (
						<>
							<section aria-label="Totals" className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
								<Tile label="Cost" value={formatUsd(totals?.costUsd)} hint={totals?.unpricedCalls ? `${calls(totals.unpricedCalls)} unpriced` : `${usage.range.from} to ${usage.range.to}`} />
								<Tile label="Calls" value={formatCount(totals?.calls)} />
								<Tile label="Tokens" value={formatTokens(tokens)} hint={`${formatTokens(totals?.outputTokens)} written`} />
								<Tile label="Cache hit" value={formatPercent(totals?.cacheHitRatio)} hint="Prompt tokens read from cache" />
								<Tile label="Latency" value={formatMs(totals?.latencyP50Ms)} hint={`p95 ${formatMs(totals?.latencyP95Ms)}`} />
								<Tile label="Errors" value={formatPercent(totals?.errorRate)} hint={`${calls(totals?.errors ?? 0)} failed`} />
							</section>

							<div className="grid gap-4 lg:grid-cols-3">
								<div className="lg:col-span-2">
									<DailyCost days={usage.byDay} />
								</div>
								<CapBar month={usage.month} />
							</div>

							<div className="grid gap-4 lg:grid-cols-2">
								<Card>
									<CardHeader className="pb-2">
										<CardTitle className="text-base">By model</CardTitle>
									</CardHeader>
									<CardContent>
										<Table>
											<TableHeader>
												<TableRow>
													<TableHead>Model</TableHead>
													<TableHead className="text-right">Calls</TableHead>
													<TableHead className="text-right">Cost</TableHead>
													<TableHead className="text-right">Cache hit</TableHead>
													<TableHead className="text-right">p50 / p95</TableHead>
												</TableRow>
											</TableHeader>
											<TableBody>
												{usage.byModel.length === 0 ? (
													<TableRow>
														<TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
															No calls in this range.
														</TableCell>
													</TableRow>
												) : (
													usage.byModel.map(row => (
														<TableRow key={row.model}>
															<TableCell className="font-medium">{row.model}</TableCell>
															<TableCell className="text-right">{formatCount(row.calls)}</TableCell>
															<TableCell className="text-right">{row.unpricedCalls === row.calls ? 'Unpriced' : formatUsd(row.costUsd)}</TableCell>
															<TableCell className="text-right">{formatPercent(row.cacheHitRatio)}</TableCell>
															<TableCell className="text-right">
																{formatMs(row.latencyP50Ms)} / {formatMs(row.latencyP95Ms)}
															</TableCell>
														</TableRow>
													))
												)}
											</TableBody>
										</Table>
									</CardContent>
								</Card>

								<Card>
									<CardHeader className="pb-2">
										<CardTitle className="text-base">Outcomes</CardTitle>
									</CardHeader>
									<CardContent>
										<Table>
											<TableHeader>
												<TableRow>
													<TableHead>Outcome</TableHead>
													<TableHead className="text-right">Calls</TableHead>
													<TableHead className="text-right">Share</TableHead>
												</TableRow>
											</TableHeader>
											<TableBody>
												{usage.byOutcome.map(row => (
													<TableRow key={row.outcome}>
														<TableCell>{OUTCOME_LABELS[row.outcome] ?? row.outcome}</TableCell>
														<TableCell className="text-right">{formatCount(row.calls)}</TableCell>
														<TableCell className="text-right">{formatPercent(row.share)}</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</CardContent>
								</Card>
							</div>

							<Card>
								<CardHeader className="pb-2">
									<CardTitle className="text-base">Top users</CardTitle>
								</CardHeader>
								<CardContent>
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead>User</TableHead>
												<TableHead className="text-right">Calls</TableHead>
												<TableHead className="text-right">Tokens</TableHead>
												<TableHead className="text-right">Cost</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{usage.topUsers.length === 0 ? (
												<TableRow>
													<TableCell colSpan={4} className="text-center text-sm text-muted-foreground">
														No calls in this range.
													</TableCell>
												</TableRow>
											) : (
												usage.topUsers.map(row => (
													<TableRow key={row.userId}>
														<TableCell>
															<span className="font-medium text-gray-900">{row.name ?? 'Deleted user'}</span>
															{row.email && <span className="block text-xs text-muted-foreground">{row.email}</span>}
														</TableCell>
														<TableCell className="text-right">{formatCount(row.calls)}</TableCell>
														<TableCell className="text-right">{formatTokens(row.tokens)}</TableCell>
														<TableCell className="text-right">{formatUsd(row.costUsd)}</TableCell>
													</TableRow>
												))
											)}
										</TableBody>
									</Table>
								</CardContent>
							</Card>
						</>
					)}
				</main>
			</div>
		</div>
	)
}
